/**
 * Keeps a hidden terminal's shell alive (Pro Builder hides its window instead
 * of closing it). The sandbox daemon reaps a PTY after 30 minutes with no
 * output, input or resize (`PTY_IDLE_TIMEOUT_MS`, eddy-sandbox/daemon/src/index.ts),
 * and a hidden window sends no resize while an idle program prints nothing.
 * Re-sending the CURRENT size resets that clock; the kernel does not signal the
 * program when the size is unchanged, so nothing repaints.
 */
export const KEEPALIVE_INTERVAL_MS = 10 * 60 * 1000;

export interface TerminalSize {
  cols: number;
  rows: number;
}

/**
 * The size a keepalive resize should carry: the last size the terminal really
 * had, or null when there is none (never opened, or only ever measured 0x0 —
 * the daemon rejects those and a tiny PTY would wreck the layout).
 */
export function keepAliveSize(last: TerminalSize | null): TerminalSize | null {
  if (!last) return null;
  const { cols, rows } = last;
  if (!Number.isInteger(cols) || !Number.isInteger(rows) || cols <= 0 || rows <= 0) return null;
  return { cols, rows };
}

/**
 * Start the keepalive loop. `send` is called every interval with the size to
 * re-send (skipped while there is none). Returns the stop function.
 */
export function startKeepAlive(
  getLastSize: () => TerminalSize | null,
  send: (size: TerminalSize) => void,
  intervalMs: number = KEEPALIVE_INTERVAL_MS,
): () => void {
  const timer = setInterval(() => {
    const size = keepAliveSize(getLastSize());
    if (size) send(size);
  }, intervalMs);
  return () => clearInterval(timer);
}
